import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import { ReactQueryDevtools } from 'react-query/devtools'
import { Toaster } from 'react-hot-toast'

import ErrorBoundary from '@/components/ErrorBoundary'
import Layout from '@/components/Layout'
import ProtectedRoute from '@/components/ProtectedRoute'
import ScrollToTop from '@/components/ScrollToTop'
import LogoSpinner from '@/components/ui/LogoSpinner'
import DashboardSkeleton from '@/components/skeletons/DashboardSkeleton'
import DirectorySkeleton from '@/components/skeletons/DirectorySkeleton'
import AlbumSkeleton from '@/components/skeletons/AlbumSkeleton'
import LandingPage from '@/pages/LandingPage'
import LoginPage from '@/pages/LoginPage'
import SignupPage from '@/pages/SignupPage'
import AuthCallbackPage from '@/pages/AuthCallbackPage'
import SetPasswordPage from '@/pages/SetPasswordPage'
import CompleteProfilePage from '@/pages/CompleteProfilePage'
import ForgotPasswordPage from '@/pages/ForgotPasswordPage'
import AcceptInvitePage from '@/pages/AcceptInvitePage'
import AccessDeniedPage from '@/pages/AccessDeniedPage'
import NotFoundPage from '@/pages/NotFoundPage'

/**
 * Wrap a dynamic import in a one-shot retry. Lazy-chunk fetches occasionally
 * fail in the wild — transient network blips, service-worker races, or a
 * stale `index.html` referencing a chunk filename that's been replaced by a
 * fresh deploy. Without this, the first navigation throws and the
 * ErrorBoundary shows "Something went wrong"; the user clicks again and it
 * works because React re-attempts the import on the next render.
 *
 * Strategy: try once; on failure wait 500ms and try again; if that ALSO
 * fails, assume the chunk is genuinely gone (stale deploy) and trigger a
 * page reload so the browser picks up the new index.html with current chunk
 * filenames. A sessionStorage flag prevents reload loops if the second deploy
 * is also broken.
 */
function lazyWithRetry<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await importer()
    } catch (_first) {
      await new Promise((resolve) => setTimeout(resolve, 500))
      try {
        return await importer()
      } catch (second) {
        const reloadKey = '__chunk_reload_attempted'
        if (
          typeof window !== 'undefined' &&
          !window.sessionStorage.getItem(reloadKey)
        ) {
          window.sessionStorage.setItem(reloadKey, '1')
          window.location.reload()
          // Hold the lazy promise forever — the reload supersedes it.
          return new Promise<{ default: T }>(() => {})
        }
        throw second
      }
    }
  })
}

// Lazy-load member pages — they aren't on the public critical path.
const DashboardPage = lazyWithRetry(() => import('@/pages/DashboardPage'))
const CalendarPage = lazyWithRetry(() => import('@/pages/CalendarPage'))
const EventsPage = lazyWithRetry(() => import('@/pages/EventsPage'))
const BulletinBoardPage = lazyWithRetry(() => import('@/pages/BulletinBoardPage'))
const DirectoryPage = lazyWithRetry(() => import('@/pages/DirectoryPage'))
const BoardPage = lazyWithRetry(() => import('@/pages/BoardPage'))
const ChapelPage = lazyWithRetry(() => import('@/pages/ChapelPage'))
const ArchivesPage = lazyWithRetry(() => import('@/pages/ArchivesPage'))
const DocumentsPage = lazyWithRetry(() => import('@/pages/DocumentsPage'))
const ProfilePage = lazyWithRetry(() => import('@/pages/ProfilePage'))
const AlbumPage = lazyWithRetry(() => import('@/pages/AlbumPage'))
const MessagesPage = lazyWithRetry(() => import('@/pages/MessagesPage'))
const AdminPage = lazyWithRetry(() => import('@/pages/AdminPage'))
const MemberProfilePage = lazyWithRetry(() => import('@/pages/MemberProfilePage'))
const HelpDeskPage = lazyWithRetry(() => import('@/pages/HelpDeskPage'))
const ShopPage = lazyWithRetry(() => import('@/pages/ShopPage'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000, // 1 minute
    },
    mutations: { retry: 0 },
  },
})

const PageFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <LogoSpinner size="lg" label="Loading" />
  </div>
)

const App = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<PageFallback />}>
            <Routes>
              {/* ---- Public routes ---- */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="/set-password" element={<SetPasswordPage />} />
              <Route path="/complete-profile" element={<CompleteProfilePage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/family-invite/:token" element={<AcceptInvitePage />} />
              <Route path="/access-denied" element={<AccessDeniedPage />} />

              {/* ---- Member-only routes ---- */}
              <Route
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route
                  path="/dashboard"
                  element={
                    <Suspense fallback={<DashboardSkeleton />}>
                      <DashboardPage />
                    </Suspense>
                  }
                />
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/events" element={<EventsPage />} />
                <Route path="/bulletin" element={<BulletinBoardPage />} />
                <Route
                  path="/directory"
                  element={
                    <Suspense fallback={<DirectorySkeleton />}>
                      <DirectoryPage />
                    </Suspense>
                  }
                />
                <Route path="/board" element={<BoardPage />} />
                <Route path="/chapel" element={<ChapelPage />} />
                <Route path="/archives" element={<ArchivesPage />} />
                <Route path="/documents" element={<DocumentsPage />} />
                <Route
                  path="/album"
                  element={
                    <Suspense fallback={<AlbumSkeleton />}>
                      <AlbumPage />
                    </Suspense>
                  }
                />
                <Route path="/messages" element={<MessagesPage />} />
                <Route path="/help" element={<HelpDeskPage />} />
                <Route path="/shop" element={<ShopPage />} />
                <Route path="/shop/checkout" element={<ShopPage />} />
                <Route path="/members/:id" element={<MemberProfilePage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute requireRole="BOARD">
                      <AdminPage />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* Catch-all → real 404 page */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>

        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: { background: '#0f172a', color: '#fff' },
            success: { duration: 3000, iconTheme: { primary: '#10B981', secondary: '#fff' } },
            error: { duration: 5000, iconTheme: { primary: '#EF4444', secondary: '#fff' } },
          }}
        />

        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
